// Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { useState, useEffect } from "react";
import { useGetApi } from "../../../data/hooks/api";
import { RESOURCE_SERVER_URLS } from "../../../data/configs/spl-config";
import DefaultTable from "../../DefaultTable";
import * as Types from "../../../data/utils/types";
import { Box, SelectChangeEvent } from '@mui/material';
import { SelectDropdown } from "../../SelectDropdown";

interface ListProjectCasesProps {
  id: string;
  isTypeCloud: boolean;
}

export default function ListProjectCases({
  id,
  isTypeCloud,
}: ListProjectCasesProps): JSX.Element {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
 
  const states: string[] = ['Open', 'Work In Progress', 'Awaiting Info', 'Solution Proposed', 'In Progress',
   'Waiting on Client', 'Waiting on WSO2', 'Closed', 'Cancelled','Reopened', 'Differed'];
  const defaultStates: string[] = localStorage.getItem('stateValues') 
    ? JSON.parse(localStorage.getItem('stateValues') as string)
    : ['Open', 'Work In Progress', 'Awaiting Info', 'Solution Proposed', 'In Progress', 'Waiting on Client', 
    'Waiting on WSO2', 'Closed'];
  const [stateValues, setStateValues] = useState<string[]>(defaultStates);

  const caseTypes: string[] = ['Query', 'Admin Task', 'Incident', 'Hosting Query', 'Cloud Incident', 
  'Cloud Query', 'Task', 'Hosting', 'Announcement', 'Engagement', 'NFR', 'Story', 'New Feature', 'Change Requests', 
  'Bug', 'Sub-Task', 'Improvement', 'Hosting Task'];
  const defaultCaseTypes: string[] = localStorage.getItem('caseTypeValues') 
    ? JSON.parse(localStorage.getItem('caseTypeValues') as string) 
    :  ['Query', 'Admin Task', 'Incident', 'Hosting Query', 'Cloud Incident','Cloud Query', 'Task', 'Hosting'];
  const [caseTypeValues, setCaseTypeValues] = useState<string[]>(defaultCaseTypes);

  useEffect(() => {
    if (isTypeCloud) {
      setCaseTypeValues([]);
    }
  },[]);

  const apiUrl =
  RESOURCE_SERVER_URLS[0] +
    `/projects/${id}/cases?offset=${page * rowsPerPage}&limit=${rowsPerPage}&stateFilters=${stateValues}&caseTypeFilters=${caseTypeValues}`;
  const { data, loading, error, getApiData } = useGetApi<
    Types.caseDetails[]
  >({ url: apiUrl, headers: { accept: "application/json" } });

  useEffect(() => {
    getApiData(apiUrl);
    if(!isTypeCloud){
      localStorage.setItem("caseTypeValues", JSON.stringify(caseTypeValues));
    }
    localStorage.setItem('stateValues', JSON.stringify(stateValues));
  }, [rowsPerPage, page, stateValues, caseTypeValues]);

  const colNameArray = ["Number", "Case ID", "Short Description", "Case Type", "Priority", "State"];
  const colAttributeArray = ["number", "caseId", "shortDescription", "caseType", "priority", "state"];

  const handleCaseTypeChange = (event: SelectChangeEvent<string[]>) => {
    const { target: { value } } = event;
    setCaseTypeValues(typeof value === 'string' ? value.split(',') : value);

  };

  const handleStateChange = (event: SelectChangeEvent<string[]>) => {
    const { target: { value } } = event;
    setStateValues(typeof value === 'string' ? value.split(',') : value);
  };

  const handleCaseTableRowClick = (rowData: Types.caseDetails) => {
    window.open(`/support/cases/${rowData.number}`,'_blank');
  };

  return (
    <Box>
      <Box paddingBottom={2}>
      {!isTypeCloud && (
          <SelectDropdown
            label="Case Type"
            allValues={caseTypes}
            values={caseTypeValues}
            handleSelectChange={handleCaseTypeChange}
          />
        )}
        <SelectDropdown
            label="State"
            allValues={states}
            values={stateValues}
            handleSelectChange={handleStateChange}
        />
      </Box>
      <Box>
        <DefaultTable
          data={data}
          loading={loading}
          error={error}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          colNameArray={isTypeCloud? colNameArray.filter((colName) => colName !== 'Case Type') : colNameArray}
          colAttributeArray={isTypeCloud? colAttributeArray.filter((colAttr) => colAttr !== 'caseType') : colAttributeArray}
          handleRowClick={handleCaseTableRowClick}
        />
      </Box>
    </Box>
  );
}
